import React, { Component } from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import DocumentTitle from 'react-document-title';
import { bindActionCreators } from 'redux';
import { map, pathOr } from 'ramda';
import moment from 'moment';
import confirm from '../../components/ConfirmDialogModal';
import * as createResidentActions from './actions';
import { getMonthlyTransactions } from '../ApplicationProfile/actions';
import { MoveInCheckApplicationScreen } from '../../components/MoveInCheck';
import { getMoveInProratedTransactions } from '../../utils/prorateCalculator';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import messages from './messages';

import {
  getApplicantName,
  getLeaseStartDate,
  getUnitNumber,
} from './selectors';
import { getSelectedPropertyClassType } from '../App/selectors';
import { generateComplianceStatusArray } from './utils';
import { navigateToUrlWithSelectedPropertyId } from '../../utils/navigation-helpers';

import type { GlobalState } from '../App/types';

import { FeatureFlags } from '../../types';

type StateProps = {
  locale: string,
  vehicleStatus?: Object,
  moveInActions?: Array<Object>,
  applicationId: string,
  checklistStatus?: Object,
  screeningStatus?: Object,
  signedLeaseStatus?: Object,
  leaseStatus?: Object,
  leaseStartDateStatus?: Object,
  unitAvailabilityStatus?: Object,
  applicantName: string,
  unitNumber: string,
  leaseStartDate: Object,
  monthlyTransactions?: Array<Object>,
  propertyClassType: string,
  currentApplication: ?Object,
  location?: any,
  leaseMoveInDate: string,
  property: Object,
};
type Props = {
  intl: any,
  propertyId: string,
  history: Object,
  store: Object,
  moveInApplyFullAmountToProRate: boolean,
  flags: Object,
  propertyDetails: Object,
  actions: {
    getChecklistStatus: (applicant: string) => Object,
    getSignedLeaseStatus: (applicant: string) => Object,
    getScreeningStatus: (applicant: string) => Object,
    getVehicleStatus: (applicant: string) => Object,
    getMoveInActionsStatus: () => Object,
    getLeaseStatus: (applicant: string) => Object,
    getLeaseStartDateStatus: (applicant: string) => Object,
    getOneApplication: (applicant: string) => void,
    getOneProperty: (applicant: string) => void,
    clean: () => void,
    moveIn: (
      applicationId: string,
      actualMoveInDate: any,
      requiredActions: Array<Object>,
      moveInWithoutScreening: boolean,
      prorated: Array<Object>,
    ) => void,
    getUnitAvailabilityStatus: (unitId: string) => Object,
    getMonthlyTransactions: (applicationId: string) => Object,
  },
};

type State = {
  actualMoveInDate: any,
  moveInActions: Array<any>,
};

export class CreateResident extends Component<StateProps & Props, State> {
  constructor(props: StateProps & Props) {
    super(props);
    this.state = {
      actualMoveInDate: moment(new Date()).format('YYYY-MM-DD'),
      moveInActions: [],
    };
  }

  componentWillMount() {
    this.props.actions.clean();
  }

  componentDidMount() {
    const applicationId: string = this.props.applicationId;
    const { leaseAppRevampTruework, leaseAppRevampTransunion } =
      this.props.flags;
    const isNewScreeningServiceOn =
      leaseAppRevampTransunion || leaseAppRevampTruework;
    this.props.actions.getVehicleStatus(applicationId);
    this.props.actions.getMoveInActionsStatus();
    this.props.actions.getOneApplication(applicationId);
    this.props.actions.getChecklistStatus(applicationId);
    this.props.actions.getSignedLeaseStatus(applicationId);
    this.props.actions.getScreeningStatus(
      applicationId,
      isNewScreeningServiceOn,
    );
    this.props.actions.getLeaseStatus(applicationId);
    this.props.actions.getLeaseStartDateStatus(applicationId);
    this.props.actions.getUnitAvailabilityStatus(applicationId);
    this.props.actions.getMonthlyTransactions(applicationId);
    if (this.props.flags.transunionOffLeaseAppOff) {
      this.props.actions.getOneProperty(applicationId);
    }
    if (isNewScreeningServiceOn) {
      this.props.actions.getNewScreeningServiceProviders();
    }
  }

  handleCancel = () => {
    const route = `/application/${this.props.applicationId}`;
    navigateToUrlWithSelectedPropertyId(route);
  };

  handleMoveInDateUpdate = (moveInActionsField: Array<any>, newDate: any) => {
    this.setState({
      actualMoveInDate: moment(newDate).format('YYYY-MM-DD'),
      moveInActions: moveInActionsField,
    });
  };

  handleSubmit = (values: any) => {
    confirm(this.props.intl.formatMessage(messages.convertConfirmation), {
      intl: this.props.intl,
    })
      .then(() => {
        const requiredActions: Array<any> = map(
          (action) => ({
            ...action,
            valid: values['moveInActions'][action.id] || true,
          }),
          this.getApplicableMoveInActions() || [],
        );
        const {
          propertyDetails,
          flags,
          newServiceHasNoActiveScreeningProviders,
        } = this.props;
        const {
          leaseAppRevampTransunion,
          leaseAppRevampTruework,
          transunionOffLeaseAppOff,
        } = flags;
        const isNewScreeningServiceOn =
          leaseAppRevampTransunion || leaseAppRevampTruework;

        const isTransUnionActive = propertyDetails?.isTransUnionActive;

        const noActiveScreeningProviders = isNewScreeningServiceOn
          ? newServiceHasNoActiveScreeningProviders
          : transunionOffLeaseAppOff && !isTransUnionActive;

        const moveInWithoutScreening = noActiveScreeningProviders
          ? true
          : values.moveInWithoutScreening;

        this.props.actions.moveIn(
          this.props.applicationId,
          values.actualMoveInDate,
          requiredActions,
          moveInWithoutScreening,
          values.prorated,
        );
      })
      .catch(() => {});
  };

  getApplicableMoveInActions = (): Array<Object> => {
    const {
      propertyDetails,
      flags,
      newServiceHasNoActiveScreeningProviders,
      moveInActions,
    } = this.props;
    const {
      leaseAppRevampTransunion,
      leaseAppRevampTruework,
      transunionOffLeaseAppOff,
    } = flags;
    const isNewScreeningServiceOn =
      leaseAppRevampTransunion || leaseAppRevampTruework;

    const isTransUnionActive = propertyDetails?.isTransUnionActive;

    const noActiveScreeningProviders = isNewScreeningServiceOn
      ? newServiceHasNoActiveScreeningProviders
      : transunionOffLeaseAppOff && !isTransUnionActive;

    return noActiveScreeningProviders
      ? moveInActions
      : moveInActions.filter(
          (action: Object) =>
            action.text !== 'Screening Completed (if applicable)',
        );
  };

  getSteps({
    isAffordable,
    isTransUnionActive,
  }: {
    isAffordable: boolean,
    isTransUnionActive: boolean,
  }) {
    let {
      checklistStatus,
      leaseStartDateStatus,
      leaseStatus,
      screeningStatus,
      signedLeaseStatus,
      vehicleStatus,
      unitAvailabilityStatus,
      currentApplication,
      property,
      flags,
      newServiceHasNoActiveScreeningProviders,
    } = this.props;

    const {
      leaseAppRevampTransunion,
      leaseAppRevampTruework,
      transunionOffLeaseAppOff,
    } = flags;
    const isNewScreeningServiceOn =
      leaseAppRevampTransunion || leaseAppRevampTruework;

    let statusArray = [];
    const complianceStatuses = generateComplianceStatusArray(
      currentApplication,
      property?.pap,
    );
    statusArray = isAffordable ? [...complianceStatuses] : [];

    const allSteps = [
      {
        ...unitAvailabilityStatus,
        title: this.props.intl.formatMessage(messages.unitAvailabilityStatus),
      },
      {
        ...checklistStatus,
        title: this.props.intl.formatMessage(messages.applicationCheckList),
      },
      {
        ...screeningStatus,
        title: this.props.intl.formatMessage(messages.householdScreening),
      },
      {
        ...leaseStatus,
        title: this.props.intl.formatMessage(messages.leaseStatus),
      },
      {
        ...signedLeaseStatus,
        title: this.props.intl.formatMessage(messages.signedLease),
      },
      {
        ...vehicleStatus,
        title: this.props.intl.formatMessage(messages.vehicleInformation),
      },
      {
        ...leaseStartDateStatus,
        title: this.props.intl.formatMessage(messages.leaseStartDateStatus),
      },

      ...statusArray,
    ];
    const stepsWithoutScreening = allSteps.filter(
      (step: Object) => step.title !== 'Household Screening',
    );

    if (isNewScreeningServiceOn) {
      return newServiceHasNoActiveScreeningProviders
        ? stepsWithoutScreening
        : allSteps;
    }

    const stepsFlagOn = isTransUnionActive ? allSteps : stepsWithoutScreening;

    return transunionOffLeaseAppOff ? stepsFlagOn : allSteps;
  }

  render() {
    const {
      applicantName,
      checklistStatus,
      intl,
      leaseStartDate,
      leaseStartDateStatus,
      leaseStatus,
      screeningStatus,
      signedLeaseStatus,
      unitNumber,
      vehicleStatus,
      unitAvailabilityStatus,
      monthlyTransactions,
      propertyClassType,
      currentApplication,
      leaseMoveInDate,
      moveInApplyFullAmountToProRate,
      propertyDetails,
      newServiceHasNoActiveScreeningProviders,
    } = this.props;

    const flags: FeatureFlags = this.props.flags;

    const { leaseAppRevampTransunion, leaseAppRevampTruework } = flags;
    const isNewScreeningServiceOn =
      leaseAppRevampTransunion || leaseAppRevampTruework;

    const applicantPropertyClass = pathOr(
      '',
      ['propertyClass', 'name'],
      currentApplication,
    );
    const isAffordable =
      propertyClassType === 'Affordable' ||
      (propertyClassType === 'Mixed' &&
        applicantPropertyClass === 'Affordable');

    const roundProratedRents = propertyDetails?.config?.roundProratedRents;

    const proratedTransactions = getMoveInProratedTransactions(
      monthlyTransactions || [],
      this.state.actualMoveInDate,
      moveInApplyFullAmountToProRate,
      false,
      roundProratedRents,
      flags?.correctProrateOnMoveinScreen ?? false,
    );
    const isTransUnionActive = propertyDetails?.isTransUnionActive;
    const moveInActions = this.getApplicableMoveInActions();

    const steps = this.getSteps({ isAffordable, isTransUnionActive });

    const show =
      vehicleStatus &&
      checklistStatus &&
      screeningStatus &&
      signedLeaseStatus &&
      leaseStatus &&
      leaseStartDateStatus &&
      moveInActions &&
      unitAvailabilityStatus &&
      monthlyTransactions;

    return (
      <DocumentTitle title="Fortress - Convert to Resident">
        {show && (
          <MoveInCheckApplicationScreen
            applicantName={applicantName}
            handleCancel={this.handleCancel}
            intl={intl}
            leaseStartDate={leaseStartDate}
            moveInActions={moveInActions}
            onSubmit={this.handleSubmit}
            steps={steps}
            unitNumber={unitNumber}
            monthlyTransactions={proratedTransactions}
            handleMoveInDateUpdate={this.handleMoveInDateUpdate}
            isTransUnionActive={isTransUnionActive}
            openFiscalPeriod={pathOr(
              null,
              ['openFiscalPeriod'],
              this.props.currentApplication,
            )}
            initialValues={{
              actualMoveInDate: this.state.actualMoveInDate,
              prorated: proratedTransactions,
              moveInActions: this.state.moveInActions,
              previousLeaseMoveOutDate: pathOr(
                null,
                ['previousLeaseMoveOutDate'],
                unitAvailabilityStatus,
              ),
            }}
            leaseMoveInDate={leaseMoveInDate}
            isAffordable={isAffordable}
            moveInApplyFullAmountToProRate={moveInApplyFullAmountToProRate}
            roundProratedRents={roundProratedRents}
            isNewScreeningServiceOn={isNewScreeningServiceOn}
            newServiceHasNoActiveScreeningProviders={
              newServiceHasNoActiveScreeningProviders
            }
          />
        )}
      </DocumentTitle>
    );
  }
}

const getActiveScreeningProviders = (newScreeningServiceProviders) => {
  return newScreeningServiceProviders.filter((provider) => provider.isActive);
};

export const mapStateToProps = (
  state: GlobalState,
  ownProps: Object,
): StateProps => {
  const { languageProvider, createResident } = state;
  // $FlowFixMe
  const leaseMoveInDate = pathOr(
    '',
    ['currentApplication', 'au', 'lease', 'moveInDate'],
    createResident,
  );
  const activeScreeningProviders = getActiveScreeningProviders(
    createResident.newScreeningServiceProviders ?? [],
  );

  return {
    applicationId: ownProps.match.params.applicationId,
    locale: languageProvider.locale,
    vehicleStatus: createResident.vehicleStatus,
    propertyDetails: createResident.propertyDetails,
    checklistStatus: createResident.checklistStatus,
    screeningStatus: createResident.screeningStatus,
    signedLeaseStatus: createResident.signedLeaseStatus,
    moveInActions: createResident.moveInActions,
    leaseStatus: createResident.leaseStatus,
    leaseStartDateStatus: createResident.leaseStartDateStatus,
    unitAvailabilityStatus: createResident.unitAvailabilityStatus,
    monthlyTransactions: createResident.monthlyTransactions,
    currentApplication: createResident.currentApplication,
    newServiceHasNoActiveScreeningProviders:
      activeScreeningProviders.length === 0,
    applicantName: getApplicantName(state, ownProps.history),
    unitNumber: getUnitNumber(state),
    leaseStartDate: getLeaseStartDate(state),
    propertyClassType: getSelectedPropertyClassType(state),
    leaseMoveInDate,
    moveInApplyFullAmountToProRate: pathOr(
      false,
      ['app', 'selectedProperty', 'moveInApplyFullAmountToProRate'],
      state,
    ),
    propertyId: state?.app?.selectedProperty?.id ?? '',
    property: state?.app?.selectedProperty,
  };
};

export function mapDispatchToProps(dispatch: any): Object {
  const actions = bindActionCreators(
    {
      ...createResidentActions,
      getMonthlyTransactions,
    },
    dispatch,
  );
  return { actions };
}

export default withLDConsumer()(
  connect(mapStateToProps, mapDispatchToProps)(injectIntl(CreateResident)),
);
